/**
 * Decide quais linhas de benefício mostrar e qual caminho de preview usar.
 * Zero matemática de conversão — a fórmula mora no BFF (`loyalty-engine`)
 * ou em `/transaction/simulate` (selo / regra-por-produto integrado).
 *
 * Catálogo: cashback/giftcard sempre; pontos e selos também (UI esconde se vazio).
 * Operador manual (integrado !== true): regra-por-produto = quantidade livre, não picker SKU.
 */
import type { Passbook, Role } from '@/shared/types';
import { isRegraIntegrado, quantityLabel } from '@/shared/lib/passbook-utils';
import { redemptionCap } from '@/shared/lib/loyalty-engine';
import { findAddField } from './operator-add-fields';

/** resgateNaoPontua só bloqueia ganho pra quem opera o balcão — outras roles (Admin, testes) seguem pontuando. */
const RESGATE_NAO_PONTUA_ROLES = new Set<Role>(['Validator', 'Operator']);

/**
 * regraCartao.resgateNaoPontua + role Validator/Operator: nunca pontua (selos/pontos/cashback),
 * em nenhum programa, mesmo sem resgate simultâneo. Outras roles ignoram a flag.
 */
export function blocksEarningForRole(passbook: Passbook, role: Role | undefined | null): boolean {
  if (!passbook.regraCartao?.resgateNaoPontua) return false;
  return Boolean(role && RESGATE_NAO_PONTUA_ROLES.has(role));
}

export type OperatorProgram = 'cashback' | 'points' | 'stamps' | 'balance' | 'giftcard';

export type PreviewChannel = 'bff' | 'simulate' | 'none';

export interface BenefitLineDef {
  id: 'balance' | 'catalog';
  label: string;
  kind: 'cash' | 'points' | 'stamps' | 'product';
  /** Valor máximo resgatável (quando aplicável). */
  maxAmount?: number;
  eligible: boolean;
  reason?: string;
  hint?: string;
  /** Liga por padrão quando elegível. */
  defaultOn: boolean;
}

export function detectOperatorProgram(passbook: Passbook): OperatorProgram {
  if (passbook.hasGiftcard) return 'giftcard';
  if (passbook.hasTransaction) return 'balance';
  if (passbook.hasCashback) return 'cashback';
  if (passbook.hasPoints) return 'points';
  return 'stamps';
}

/** Pontos/cashback → BFF; selo com fórmula de valor → simulate; saldo → sem conversão. */
export function resolvePreviewChannel(passbook: Passbook): PreviewChannel {
  const program = detectOperatorProgram(passbook);
  if (program === 'balance' || program === 'giftcard') return 'none';
  if (program === 'stamps') {
    // Quantidade livre / regra:0 → preview local (stampCount). Fórmulas de valor → simulate.
    if (usesManualStampCount(passbook)) return 'none';
    if (usesProductDetailsInput(passbook)) return 'simulate';
    return 'simulate';
  }
  if (usesProductDetailsInput(passbook)) return 'simulate';
  if (program === 'cashback' || program === 'points') return 'bff';
  return 'simulate';
}

/** Tipos de selo/pontuação por SKU (user.js: regra-por-produto; dump também tem regra-de-produto). */
export function isProductRuleTipo(tipo: string | undefined | null): boolean {
  return tipo === 'regra-por-produto' || tipo === 'regra-de-produto';
}

/**
 * Selo fixo / régua. Legado e produção usam `regra-fixa` (11 lojas Operator).
 * `regra-taxa` é typo antigo no fixe-admin — mantido só como alias defensivo.
 */
export function isFixedStampTipo(tipo: string | undefined | null): boolean {
  return tipo === 'regra-fixa' || tipo === 'regra-por-valor-fixo' || tipo === 'regra-taxa';
}

/** addFields.moneyValorCompra com display (legado partials/addFields.ejs). */
export function hasMoneyValorCompraField(passbook: Passbook): boolean {
  const field = findAddField(passbook, 'moneyValorCompra');
  if (!field) return false;
  const d = field.display;
  if (d === false || d === 'false') return false;
  return true;
}

/**
 * Exibe lista de produtos no resgate.
 * Pontos/selos/cashback/giftcard/balance — UI esconde se catálogo vazio.
 */
export function showsRedemptionCatalog(passbook: Passbook): boolean {
  const program = detectOperatorProgram(passbook);
  return (
    program === 'points' ||
    program === 'stamps' ||
    program === 'cashback' ||
    program === 'giftcard' ||
    program === 'balance'
  );
}

/** redeemType=discount → "desconto"; product/default → "produto". */
export function catalogRedemptionLabel(passbook: Passbook): string {
  const type = passbook.redeemType?.trim().toLowerCase();
  return type === 'discount' ? 'Resgatar desconto do catálogo' : 'Resgatar produto do catálogo';
}

export function buildBenefitLines(opts: {
  passbook: Passbook;
  balance: number;
  purchaseValue?: number;
  redeemedToday?: boolean;
}): BenefitLineDef[] {
  const { passbook, balance, purchaseValue = 0, redeemedToday } = opts;
  const program = detectOperatorProgram(passbook);
  const label = quantityLabel(passbook);
  const rc = passbook.regraCartao;
  const lines: BenefitLineDef[] = [];
  const cashLimit = rc?.cashLimit;
  const limitPct =
    cashLimit == null || !Number.isFinite(Number(cashLimit))
      ? 100
      : Math.min(100, Math.max(0, Number(cashLimit)));
  const capped = redemptionCap({
    balance,
    cashLimit,
    purchase: purchaseValue,
    program,
  });
  const cashHint =
    limitPct < 100 ? `Limite de uso: ${limitPct}% do saldo` : undefined;

  if (program === 'stamps') {
    const max = passbook.maxInputValidation ?? 10;
    if (showsRedemptionCatalog(passbook)) {
      lines.push({
        id: 'catalog',
        label: catalogRedemptionLabel(passbook),
        kind: 'product',
        maxAmount: capped > 0 ? capped : max,
        eligible: balance > 0 && !(rc?.oneOrderPerDay && redeemedToday),
        reason:
          balance <= 0
            ? `Sem ${label.toLowerCase()} disponível`
            : redeemedToday
              ? 'Já houve resgate hoje'
              : undefined,
        defaultOn: false,
      });
    }
  } else if (program === 'cashback' || program === 'giftcard' || program === 'balance') {
    const minResgate = rc?.validaValorResgate ? Number(rc.valorResgate) || 0 : 0;
    const maxAmount = capped;
    const eligible =
      maxAmount > 0 && maxAmount >= minResgate && !(rc?.oneOrderPerDay && redeemedToday);
    lines.push({
      id: 'balance',
      label: `Usar ${label.toLowerCase()} como desconto`,
      kind: 'cash',
      maxAmount,
      eligible,
      hint: cashHint,
      reason: eligible
        ? undefined
        : balance <= 0
          ? `Sem ${label.toLowerCase()} disponível`
          : maxAmount < minResgate
            ? `Mínimo para resgate: ${minResgate.toFixed(2).replace('.', ',')}`
            : 'Já houve resgate hoje',
      defaultOn: eligible,
    });
    if (showsRedemptionCatalog(passbook)) {
      lines.push({
        id: 'catalog',
        label: catalogRedemptionLabel(passbook),
        kind: 'product',
        maxAmount: capped,
        eligible: capped > 0 && !(rc?.oneOrderPerDay && redeemedToday),
        hint: cashHint,
        reason:
          balance <= 0
            ? `Sem ${label.toLowerCase()} disponível`
            : redeemedToday
              ? 'Já houve resgate hoje'
              : undefined,
        defaultOn: false,
      });
    }
  } else if (program === 'points') {
    const minResgate = rc?.validaValorResgate ? Number(rc.valorResgate) || 0 : 0;
    const freeEligible =
      capped > 0 && capped >= minResgate && !(rc?.oneOrderPerDay && redeemedToday);
    // Sem pratileira: resgate livre. Com pratileira ou catálogo: também oferece lista.
    if (!passbook.regraCartao?.pratileira) {
      lines.push({
        id: 'balance',
        label: `Resgatar ${label.toLowerCase()}`,
        kind: 'points',
        maxAmount: capped,
        eligible: freeEligible,
        hint: cashHint,
        reason: freeEligible
          ? undefined
          : balance <= 0
            ? `Sem ${label.toLowerCase()} disponível`
            : capped < minResgate
              ? `Mínimo para resgate: ${minResgate}`
              : 'Já houve resgate hoje',
        defaultOn: freeEligible,
      });
    }
    if (showsRedemptionCatalog(passbook)) {
      lines.push({
        id: 'catalog',
        label: catalogRedemptionLabel(passbook),
        kind: 'product',
        maxAmount: capped,
        eligible: capped > 0 && !(rc?.oneOrderPerDay && redeemedToday),
        hint: cashHint,
        reason:
          balance <= 0
            ? `Sem ${label.toLowerCase()} disponível`
            : redeemedToday
              ? 'Já houve resgate hoje'
              : undefined,
        defaultOn: false,
      });
    }
  }

  return lines;
}

export function usesPurchaseValueInput(passbook: Passbook): boolean {
  const program = detectOperatorProgram(passbook);
  if (program === 'cashback' || program === 'balance' || program === 'giftcard') return true;
  if (usesProductDetailsInput(passbook)) return true;
  if (program === 'points') return true;
  if (hasMoneyValorCompraField(passbook)) return true;
  const tipo = passbook.regraCartao?.tipo;
  return (
    tipo === 'regra-de-valor' ||
    tipo === 'regra-por-valor' ||
    isFixedStampTipo(tipo) ||
    Boolean(passbook.roleStamps)
  );
}

/** Valor obrigatório para avançar Conta (giftcard/saldo = só resgate no legado). */
export function requiresPurchaseValue(passbook: Passbook): boolean {
  const program = detectOperatorProgram(passbook);
  const money = findAddField(passbook, 'moneyValorCompra');
  if (program === 'giftcard' || program === 'balance') {
    return Boolean(money?.required);
  }
  // Selos: moneyValorCompra opcional no addFields não bloqueia avanço.
  if (program === 'stamps' && hasMoneyValorCompraField(passbook)) {
    return Boolean(money?.required);
  }
  return usesPurchaseValueInput(passbook);
}

/**
 * Selos com redeemType=discount: resgate é sempre um desconto sobre uma compra —
 * não existe "resgatar sem comprar" nesse tipo, então o toggle Só-resgate nem aparece.
 */
export function isStampsDiscountRedemption(passbook: Passbook): boolean {
  const type = passbook.redeemType?.trim().toLowerCase();
  return detectOperatorProgram(passbook) === 'stamps' && type === 'discount';
}

/**
 * Picker de SKU no Operador: só PDV integrado (de-para) ou bônus cashback por produto.
 * regra-por-produto com integrado false → quantidade livre (usesManualStampCount).
 */
export function usesProductDetailsInput(passbook: Passbook): boolean {
  if (!isProductRuleTipo(passbook.regraCartao?.tipo)) return false;
  return isRegraIntegrado(passbook);
}

/**
 * Cashback com bônus por SKU (user.js ~5576): details opcional —
 * sem SKU ainda pontua o valor; com SKU soma additionalInPercentage.
 */
export function usesOptionalProductDetails(passbook: Passbook): boolean {
  if (usesProductDetailsInput(passbook)) return false;
  return Boolean(passbook.hasCashback && passbook.regraCartao?.hasProductsByAdditional);
}

export function usesManualStampCount(passbook: Passbook): boolean {
  if (detectOperatorProgram(passbook) !== 'stamps') return false;
  if (passbook.disableQtde) return false;
  if (usesProductDetailsInput(passbook)) return false;
  const tipo = (passbook.regraCartao?.tipo ?? '').trim();
  const regra = Number(passbook.regraCartao?.regra);
  // regra:0 ou tipo vazio → quantidade livre. regra-por-produto manual → idem.
  if (isProductRuleTipo(tipo) && !isRegraIntegrado(passbook)) return true;
  if (!tipo || regra === 0) return true;
  if (passbook.roleStamps) return false;
  return false;
}

/**
 * Conta “só resgate”: APIs exigem valor > 0 — envia o piso monetário (R$ 0,01).
 * Sem lançamento de fidelidade (awardUnits = 0).
 */
export const OPERATOR_REDEMPTION_ONLY_PURCHASE = 0.01;
